/**
 * Mercado entre humanos: propostas, contrapropostas, leilões e veto.
 *
 * Rotas em API 10, regras anticonluio em SECURITY 5, colunas em DATABASE 2.7.
 *
 * Atenção ao vocabulário. Este é o mercado da liga, que vive na tabela `offers`
 * e conhece o veto do comissário, por isso os estados são `open`, `accepted`,
 * `rejected`, `countered`, `expired` e `vetoed`. A proposta que o motor guarda
 * dentro do mundo tem outro conjunto, em português e sem veto, e está em
 * `world.ts`. Os dois existem porque os documentos decidiram os dois; unificar
 * é decisão de projeto, não deste pacote.
 */
import { z } from "zod";
import { IdSchema, IsoDateTimeSchema, MoneySchema } from "./common.js";
/** Estado de uma proposta na liga (DATABASE 2.7). */
export const LeagueOfferStatusSchema = z.enum([
    "open",
    "accepted",
    "rejected",
    "countered",
    "expired",
    "vetoed",
]);
/**
 * Empréstimo embutido em uma proposta.
 *
 * Mesmos números do `LoanTerms` do motor, sem o `ownerId`, que é o clube que
 * está vendendo.
 */
export const OfferLoanTermsSchema = z
    .object({
    salaryShare: z.number().finite().min(0).max(1),
    untilSeason: z.number().int().min(0),
})
    .strict();
/** Proposta como a liga a guarda e a devolve. */
export const LeagueOfferSchema = z.object({
    id: IdSchema,
    leagueId: IdSchema,
    playerId: IdSchema,
    playerName: z.string().min(1),
    fromClubId: IdSchema,
    /** `null` quando o alvo é um jogador livre. */
    toClubId: IdSchema.nullable(),
    amount: MoneySchema,
    installments: z.number().int().min(1).max(3),
    /** Jogador oferecido na troca, quando houver. */
    exchangePlayerId: IdSchema.nullable(),
    loan: OfferLoanTermsSchema.nullable(),
    status: LeagueOfferStatusSchema,
    counterAmount: MoneySchema.nullable(),
    createdRound: z.number().int().min(0),
    closesAtRound: z.number().int().min(0),
    createdAt: IsoDateTimeSchema,
});
/** `POST /leagues/:id/offers`. */
export const CreateOfferRequestSchema = z
    .object({
    playerId: IdSchema,
    toClubId: IdSchema,
    amount: MoneySchema,
    installments: z.number().int().min(1).max(3),
    playerInExchange: IdSchema.optional(),
    loan: OfferLoanTermsSchema.optional(),
})
    .strict();
/** `POST /leagues/:id/offers/:offerId/respond`. */
export const RespondOfferRequestSchema = z
    .object({
    action: z.enum(["accept", "reject", "counter"]),
    /** Obrigatório apenas em `counter`. A regra fica no `service.ts`. */
    amount: MoneySchema.optional(),
})
    .strict();
/** `POST /leagues/:id/offers/:offerId/veto`, só para o comissário. */
export const VetoOfferRequestSchema = z
    .object({ reason: z.string().trim().max(200).optional() })
    .strict();
/** Direção de uma proposta em relação ao meu clube. */
export const OfferDirectionSchema = z.enum(["in", "out"]);
/**
 * `GET /leagues/:id/offers?direction=in,out&status=`.
 *
 * `direction` chega como lista separada por vírgula, do jeito que o documento
 * escreve, e sai daqui como arranjo.
 */
export const OfferListQuerySchema = z
    .object({
    direction: z
        .preprocess((value) => typeof value === "string"
        ? value
            .split(",")
            .map((part) => part.trim())
            .filter((part) => part.length > 0)
        : value, z.array(OfferDirectionSchema).min(1).max(2))
        .optional(),
    status: LeagueOfferStatusSchema.optional(),
})
    .strict();
// ---------------------------------------------------------------------------
// Leilões
// ---------------------------------------------------------------------------
export const AuctionStatusSchema = z.enum(["open", "closed"]);
/** Leilão aberto na liga (DATABASE 2.7). */
export const AuctionSchema = z.object({
    id: IdSchema,
    leagueId: IdSchema,
    playerId: IdSchema,
    playerName: z.string().min(1),
    /** Clube que colocou o jogador em leilão. `null` para jogador livre. */
    fromClubId: IdSchema.nullable(),
    minBid: MoneySchema,
    closesAtRound: z.number().int().min(0),
    status: AuctionStatusSchema,
    winnerClubId: IdSchema.nullable(),
    winningAmount: MoneySchema.nullable(),
});
/** `POST /leagues/:id/auctions/:auctionId/bid`. Lance fechado. */
export const AuctionBidRequestSchema = z.object({ amount: MoneySchema }).strict();
//# sourceMappingURL=market.js.map